import { useMemo } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BrokerClientApi } from '@apply/api';
import { applicationInsights } from '../../../../../../ApplicationInsights';
import { snackbarError } from '../../../../../../common/functions/snackbarFunctions';
import { createSearchDataSource } from '../functions/createSearchDataSource';

const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);

export interface BrokerAgreementBroker {
    id: number;
    businessName?: string;
    fullName?: string;
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    email?: string;
    phone?: string;
}

export interface BrokerAgreementBorrower {
    borrowerEntity?: string;
    name?: string;
    tin?: string;
    businessAddress?: { street1?: string; street2?: string; city?: string; state?: string; zipCode?: string };
    email?: string;
    phone?: string;
    userName?: string;
    netWorth?: number;
    adjustedNetWorth?: number;
}

/** A B-Client agreement's details, for the details card and the "Update Primary Entity" choices. */
export interface BrokerAgreementSummary {
    brokerAgreementId: number;
    /** The agreement's primary entity. */
    entityId: number;
    broker?: BrokerAgreementBroker;
    borrower?: BrokerAgreementBorrower;
    borrowerEntities?: { entityId: number; entityName: string }[];
}

/** What the dropdown needs from a search result or a loaded agreement. */
export interface BrokerAgreementOption {
    id: number;
    displayName: string;
}

/** As the old BrokerAgreementSelect showed it: "Contact - Business,  Entity - Borrower". */
const formatAgreement = (contact?: string, business?: string, entity?: string, borrowerName?: string) =>
    `${contact} - ${business ?? ''},  ${entity} - ${borrowerName}`;

const brokerAgreementSummaryQuery = (brokerAgreementId: number) =>
    queryOptions({
        queryKey: ['brokerAgreementSummary', brokerAgreementId],
        queryFn: async (): Promise<BrokerAgreementSummary> => {
            const response = await brokerClientApi.getBrokerAgreementSummary(brokerAgreementId);
            // The API's type says a one-item tuple, but it returns the summary itself
            if (response.statusCode === 200) return response.result as unknown as BrokerAgreementSummary;

            throw new Error('Failed to retrieve B-Client agreement.');
        },
    });

/** The selected agreement, for the details card. */
export const useBrokerAgreementSummaryQuery = (brokerAgreementId: number | undefined) =>
    useQuery({ ...brokerAgreementSummaryQuery(brokerAgreementId ?? 0), enabled: !!brokerAgreementId });

/** Searches B-Client agreements; the selected one comes from the same cache as the details card. */
export const useBrokerAgreementSearch = () => {
    const queryClient = useQueryClient();
    return useMemo(
        () =>
            createSearchDataSource<BrokerAgreementOption>({
                key: 'id',
                search: async (searchText, page, pageSize) => {
                    const response = await brokerClientApi.searchBrokerAgreements(searchText, page, pageSize);
                    if (response.statusCode !== 200) return [];

                    return response.result.map((item) => ({
                        id: item.id,
                        displayName: formatAgreement(
                            item.brokerContactName,
                            item.brokerBusinessName,
                            item.borrowerEntity,
                            item.borrowerName
                        ),
                    }));
                },
                getByKey: async (brokerAgreementId) => {
                    const summary = await queryClient.ensureQueryData(brokerAgreementSummaryQuery(brokerAgreementId));
                    return {
                        id: summary.brokerAgreementId,
                        displayName: formatAgreement(
                            summary.broker?.fullName,
                            summary.broker?.businessName,
                            summary.borrower?.borrowerEntity,
                            summary.borrower?.name
                        ),
                    };
                },
            }),
        [queryClient]
    );
};

/**
 * Saves the agreement's primary entity straight away (not with the loan), as the old BrokerAgreementSelect did. On
 * success the cached summary takes the new entity, so the card shows its name.
 */
export const useUpdateBrokerAgreementEntityMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ brokerAgreementId, entityId }: { brokerAgreementId: number; entityId: number; entityName: string }) => {
            const response = await brokerClientApi.updateBrokerAgreementEntity({ brokerAgreementId, entityId });
            if (response.statusCode !== 200) {
                throw new Error('Could not update the entity.');
            }
        },
        onSuccess: (_, { brokerAgreementId, entityId, entityName }) =>
            queryClient.setQueryData(brokerAgreementSummaryQuery(brokerAgreementId).queryKey, (summary) =>
                summary && { ...summary, entityId, borrower: { ...summary.borrower, borrowerEntity: entityName } }
            ),
        onError: () => snackbarError('Could not update the entity.'),
    });
};
